import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Divider,
  Group,
  Loader,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArrowBackUp,
  IconArrowLeft,
  IconArrowRight,
  IconHandGrab,
  IconMapPinOff,
  IconFileText,
  IconTruckDelivery,
  IconPackages,
} from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ApiError } from '../../shared/api/problem';
import { useViewport } from '../../shared/hooks/useViewport';
import { usePermissions } from '../auth/usePermissions';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listLocations } from '../locations/api';
import { listLicensePlates } from '../licensePlates/api';
import { PickStatusBadge } from './PickStatusBadge';
import { PickLineRow } from './PickLineRow';
import { ShipModal } from './ShipModal';
import {
  closePickShort,
  getDirectedPick,
  getPick,
  isFinishedPick,
  isReadyToShip,
  listPickLines,
  pickLicensePlate,
  pickLine,
  reversePickLine,
  type DirectedPickLineDto,
  type WalkDirection,
} from './api';

/**
 * Working a pick — a grid at a desk, one stop at a time on a handheld.
 *
 * **The grid is the primary view, and that is the correction.** M5.4 built this as a handheld
 * screen with a read-only list underneath, which left a screen that shows you the job and cannot do
 * it. legacy's OrderPickController is a DESKTOP grid with a Pick and a Reverse button on every row
 * and editable quantity, lot and from-location — picking at a desk is how the current product is
 * used, and the handheld is the second way in rather than the only one.
 *
 * So: at tablet width and above the lines are a grid you work straight down. On a phone the same
 * pick becomes one big stop at a time, in walk order, because a list of twenty on a four-inch
 * screen in a cold aisle is a list nobody reads.
 *
 * **The dock is chosen once.** legacy captures PickStagingLocationID from the first line picked and
 * never asks again, so the picker is asked for it exactly when it is a real decision.
 *
 * **Direction refetches** rather than reversing the array here: the server puts unsequenced faces
 * last in both directions, and a local reverse would move them to the front.
 */
export function DirectedPickRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const warehouseId = useCurrentWarehouseId();
  const formFactor = useViewport();
  const { id } = useParams({ from: '/app/picks/$id' });

  const handheld = formFactor === 'handheld';
  const canPost = permissions.has('picks:post');
  // Shipping is an ORDER decision, not a picking one: it writes QuantityShipped and settles
  // whether the order is finished or a back order. The server asks for orders:write.
  const canShip = permissions.has('orders:write');

  const [direction, setDirection] = useState<WalkDirection>('F');
  const [stop, setStop] = useState(0);
  const [staging, setStaging] = useState<string | null>(null);
  const [handheldQty, setHandheldQty] = useState<number | string>('');
  // The handheld had no lot box at all, so a lot-controlled item could not be picked from the
  // device the warehouse actually uses -- first with "Only 0 available" (the 083 defect) and
  // then, once the server started asking, with "scan the lot" and nowhere to type it.
  const [handheldLot, setHandheldLot] = useState('');
  // And no serial box either, which was worse: until 086 the server answered 501 and the
  // scanner could not pick a serialized item at all, by any route.
  const [handheldSerial, setHandheldSerial] = useState('');
  const [shipOpen, { open: openShip, close: closeShip }] = useDisclosure(false);
  const [plateId, setPlateId] = useState<string | null>(null);

  const pick = useQuery({
    queryKey: ['pick', id] as const,
    queryFn: ({ signal }) => getPick(id, signal),
  });

  const route = useQuery({
    queryKey: ['pick', id, 'directed', direction] as const,
    queryFn: ({ signal }) => getDirectedPick(id, direction, signal),
  });

  const document = useQuery({
    queryKey: ['pick', id, 'lines'] as const,
    queryFn: ({ signal }) => listPickLines(id, signal),
  });

  // Open plates in this warehouse, for the handheld's one-scan path. Only fetched where the
  // control exists — a desk works the grid and never needs the list.
  const plates = useQuery({
    queryKey: ['license-plates', warehouseId, 'for-picking'] as const,
    queryFn: ({ signal }) =>
      listLicensePlates({ warehouseId, status: 'open', take: 200 }, signal),
    enabled: handheld && canPost && warehouseId !== '',
  });

  const locations = useQuery({
    queryKey: ['locations', warehouseId, 'for-picking'] as const,
    queryFn: ({ signal }) => listLocations({ warehouseId }, signal),
    enabled: canPost && warehouseId !== '',
  });

  const stops = useMemo(() => route.data ?? [], [route.data]);
  const lines = useMemo(() => document.data ?? [], [document.data]);

  // The dock the server has already settled wins over anything chosen here: after the first line
  // it is not a choice any more.
  const dock = pick.data?.pickStagingLocationId ?? staging;

  const locationOptions = useMemo(
    () =>
      (locations.data ?? []).map((l) => ({
        value: l.locationId,
        label: l.displayName ? `${l.locationCode} — ${l.displayName}` : l.locationCode,
      })),
    [locations.data],
  );

  // Back to the first stop whenever the route changes under us — turning round puts you at the
  // start of the new direction, not at whatever index you happened to be on.
  useEffect(() => {
    setStop(0);
  }, [direction, stops.length]);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['pick', id] });
    void queryClient.invalidateQueries({ queryKey: ['picks'] });
  }

  function report(error: Error) {
    notifications.show({
      color: 'red',
      title: t('picks.errors.pickFailed'),
      // The server's own message names the line and the number that is left, which is more use
      // than anything composed here.
      message: error instanceof ApiError ? (error.problem.detail ?? error.message) : error.message,
    });
  }

  const doPick = useMutation({
    mutationFn: (input: {
      lineId: string;
      quantity: number;
      fromLocationId: string;
      lotNumber?: string;
      serialNumber?: string;
    }) =>
      pickLine(id, input.lineId, {
        quantity: input.quantity,
        fromLocationId: input.fromLocationId,
        toLocationId: dock!,
        lotNumber: input.lotNumber ?? null,
        serialNumber: input.serialNumber ?? null,
      }),
    onSuccess: () => {
      setHandheldQty('');
      setHandheldLot('');
      setHandheldSerial('');
      refresh();
    },
    onError: report,
  });

  const doReverse = useMutation({
    mutationFn: (input: {
      lineId: string;
      quantity: number;
      lotNumber?: string;
      serialNumber?: string;
    }) =>
      reversePickLine(id, input.lineId, {
        quantity: input.quantity,
        lotNumber: input.lotNumber ?? null,
        serialNumber: input.serialNumber ?? null,
      }),
    onSuccess: () => {
      setHandheldLot('');
      setHandheldSerial('');
      refresh();
    },
    onError: report,
  });

  const doPickPlate = useMutation({
    mutationFn: () => pickLicensePlate(id, { licensePlateId: plateId!, toLocationId: dock! }),
    onSuccess: () => {
      setPlateId(null);
      refresh();
    },
    onError: report,
  });

  const doCloseShort = useMutation({
    mutationFn: () => closePickShort(id),
    onSuccess: (updated) => {
      notifications.show({
        color: updated.status === 'cancelled' ? 'orange' : 'green',
        message:
          updated.status === 'cancelled'
            ? t('picks.closedShortNothing')
            : t('picks.closedShortSome'),
      });
      refresh();
    },
    onError: report,
  });

  const busy =
    doPick.isPending || doReverse.isPending || doCloseShort.isPending || doPickPlate.isPending;
  const finished = pick.data ? isFinishedPick(pick.data.status) : false;
  const current: DirectedPickLineDto | undefined = stops[stop];
  const currentLine = current ? lines.find((l) => l.pickLineId === current.pickLineId) : undefined;

  return (
    <>
      <PageHeader
        title={pick.data ? pick.data.pickNumber : t('nav.picks')}
        actions={
          <Group gap="sm">
            {canShip && pick.data && isReadyToShip(pick.data) && (
              <Button
                color="invetrak"
                leftSection={<IconTruckDelivery size={16} />}
                onClick={openShip}
              >
                {t('picks.ship')}
              </Button>
            )}
            {canPost && pick.data && !finished && (
              <Button
                variant="default"
                color="orange"
                loading={doCloseShort.isPending}
                onClick={() => doCloseShort.mutate()}
              >
                {t('picks.closeShort')}
              </Button>
            )}
            <Button
              variant="default"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => void navigate({ to: '/app/picks' })}
            >
              {t('picks.backToList')}
            </Button>
          </Group>
        }
      />

      <Stack gap="md">
        {(pick.isLoading || document.isLoading) && (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        )}

        {(pick.isError || document.isError) && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('picks.errors.loadFailed')}
          >
            <Stack gap="xs">
              <Text size="sm">{((pick.error ?? document.error) as Error | null)?.message}</Text>
              {pick.error instanceof ApiError && pick.error.problem.correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{pick.error.problem.correlationId}</Code>
                </Text>
              )}
              <Group>
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => {
                    void pick.refetch();
                    void document.refetch();
                  }}
                >
                  {t('picks.retry')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {pick.isSuccess && pick.data.isShipped && (
          <Alert icon={<IconTruckDelivery size={16} />} color="green" variant="light">
            <Stack gap="sm" align="flex-start">
              <Text size="sm">
                {t('picks.alreadyShipped', {
                  when: pick.data.shippedAtUtc
                    ? new Date(pick.data.shippedAtUtc).toLocaleString()
                    : '',
                })}
                {pick.data.trackingNumber
                  ? ` ${t('picks.tracking')}: ${pick.data.trackingNumber}`
                  : ''}
              </Text>
              {/*
                Here rather than only on the list, because this is where somebody lands the moment
                they confirm a shipment -- and the packing list is the next thing they need, while
                the boxes are still open in front of them.
              */}
              <Button
                size="xs"
                variant="default"
                leftSection={<IconFileText size={16} />}
                onClick={() =>
                  void navigate({ to: '/app/picks/$id/packing-list', params: { id } })
                }
              >
                {t('picks.packing.title')}
              </Button>
            </Stack>
          </Alert>
        )}

        {pick.isSuccess && (
          <Card withBorder padding="md">
            <Group justify="space-between" wrap="wrap" gap="sm">
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  {t('picks.forOrder')}
                </Text>
                <Group gap="xs">
                  {pick.data.salesOrderId ? (
                    <Anchor
                      fw={500}
                      onClick={() =>
                        void navigate({
                          to: '/app/sales-orders/$id',
                          params: { id: pick.data.salesOrderId! },
                        })
                      }
                    >
                      <Code>{pick.data.orderNumber}</Code>
                    </Anchor>
                  ) : (
                    <Text>—</Text>
                  )}
                  <Text>{pick.data.customerName ?? '—'}</Text>
                </Group>
                {pick.data.customerPo && (
                  <Text size="sm" c="dimmed">
                    {t('picks.customerPo')}: {pick.data.customerPo}
                  </Text>
                )}
              </Stack>
              <Stack gap={4} align="flex-end">
                <Group gap="xs">
                  <PickStatusBadge status={pick.data.status} />
                  {pick.data.isClosedShort && (
                    <Badge color="orange" variant="light">
                      {t('picks.shortBadge')}
                    </Badge>
                  )}
                </Group>
                <Text size="sm" c="dimmed">
                  {t('picks.linesDone', {
                    done: pick.data.lineCount - pick.data.linesOutstanding,
                    total: pick.data.lineCount,
                  })}
                </Text>
                {pick.data.pickStagingLocationCode && (
                  <Text size="sm">
                    {t('picks.goingTo')}: <Code>{pick.data.pickStagingLocationCode}</Code>
                  </Text>
                )}
              </Stack>
            </Group>
          </Card>
        )}

        {/*
          The dock. Asked for once, before anything is picked, because legacy captures it from the
          first line and never asks again — so this is the only moment it is a real decision.
        */}
        {canPost && pick.isSuccess && !finished && !pick.data.pickStagingLocationId && (
          <Card withBorder padding="md">
            <Stack gap="xs">
              <Select
                label={t('picks.staging')}
                description={t('picks.stagingHint')}
                placeholder={t('picks.chooseStaging')}
                data={locationOptions}
                value={staging}
                onChange={setStaging}
                searchable
                w={{ base: '100%', sm: 360 }}
              />
              {staging === null && (
                <Text size="sm" c="orange">
                  {t('picks.stagingRequired')}
                </Text>
              )}
            </Stack>
          </Card>
        )}

        {!canPost && pick.isSuccess && !finished && (
          <Alert color="gray" variant="light">
            {t('picks.readOnly')}
          </Alert>
        )}

        {document.isSuccess && lines.length === 0 && (
          <Alert color="gray" variant="light">
            {t('picks.noLines')}
          </Alert>
        )}

        {/* ---------------- desktop and tablet: the grid ---------------- */}
        {!handheld && document.isSuccess && lines.length > 0 && (
          <Card withBorder padding="md">
            <Table.ScrollContainer minWidth={900}>
              <Table striped highlightOnHover aria-label={t('picks.document')}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('picks.line')}</Table.Th>
                    <Table.Th>{t('picks.item')}</Table.Th>
                    <Table.Th>{t('picks.requested')}</Table.Th>
                    <Table.Th>{t('picks.picked')}</Table.Th>
                    <Table.Th>{t('picks.outstanding')}</Table.Th>
                    <Table.Th>{t('picks.quantity')}</Table.Th>
                    <Table.Th>{t('picks.from')}</Table.Th>
                    {lines.some((l) => l.isLotControlled) && <Table.Th>{t('picks.lot')}</Table.Th>}
                    {lines.some((l) => l.isSerialized) && <Table.Th>{t('picks.serial')}</Table.Th>}
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {lines.map((line) => (
                    <PickLineRow
                      key={line.pickLineId}
                      line={line}
                      suggested={stops.find((s) => s.pickLineId === line.pickLineId)}
                      locations={locationOptions}
                      stagingLocationId={dock}
                      disabled={!canPost || finished}
                      busy={busy}
                      onPick={(input) =>
                        doPick.mutate({ lineId: line.pickLineId, ...input })
                      }
                      onReverse={(quantity, lotNumber, serialNumber) =>
                        doReverse.mutate({
                          lineId: line.pickLineId, quantity, lotNumber, serialNumber,
                        })
                      }
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Card>
        )}

        {/* ---------------- handheld: one stop at a time ---------------- */}
        {handheld && route.isSuccess && stops.length === 0 && (
          <Alert color="green" variant="light">
            {t('picks.routeFinished')}
          </Alert>
        )}

        {handheld && route.isSuccess && stops.length > 0 && current && (
          <>
            <Group justify="space-between" wrap="wrap" gap="sm">
              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  {t('picks.direction')}
                </Text>
                <SegmentedControl
                  value={direction}
                  onChange={(v) => setDirection(v as WalkDirection)}
                  data={[
                    { label: t('picks.forward'), value: 'F' },
                    { label: t('picks.reverse'), value: 'R' },
                  ]}
                />
              </Stack>
              <Text size="sm" c="dimmed">
                {t('picks.stopOf', { index: stop + 1, total: stops.length })}
              </Text>
            </Group>

            <Card withBorder padding="lg" radius="md">
              <Stack gap="md">
                {current.suggestedLocationCode ? (
                  <Stack gap={2}>
                    <Text size="sm" c="dimmed" tt="uppercase">
                      {t('picks.goTo')}
                    </Text>
                    {/*
                      Scales with the viewport. A location code is varchar(50) and a fixed 2.5rem
                      pushes a long one off the side of a 375px phone — which is the one screen
                      width this card exists for.
                    */}
                    <Title
                      order={1}
                      style={{
                        fontSize: 'clamp(1.75rem, 9vw, 2.5rem)',
                        lineHeight: 1.1,
                        wordBreak: 'break-word',
                      }}
                    >
                      {current.suggestedLocationCode}
                    </Title>
                  </Stack>
                ) : (
                  <Alert
                    icon={<IconMapPinOff size={16} />}
                    color="orange"
                    variant="light"
                    title={t('picks.noPickFaceTitle')}
                  >
                    {t('picks.noPickFaceBody')}
                  </Alert>
                )}

                <Divider />

                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <Stack gap={2}>
                    <Text size="sm" c="dimmed" tt="uppercase">
                      {t('picks.item')}
                    </Text>
                    <Title order={3}>
                      <Code style={{ fontSize: '1.25rem' }}>{current.itemNumber}</Code>
                    </Title>
                    <Text>{current.itemDescription}</Text>
                    <Group gap="xs" mt={4}>
                      {current.isLotControlled && (
                        <Badge color="grape" variant="light">
                          {t('picks.lotControlled')}
                        </Badge>
                      )}
                      {current.isSerialized && (
                        <Badge color="indigo" variant="light">
                          {t('picks.serialized')}
                        </Badge>
                      )}
                    </Group>
                  </Stack>

                  <Stack gap={2}>
                    <Text size="sm" c="dimmed" tt="uppercase">
                      {t('picks.take')}
                    </Text>
                    <Group gap="xs" align="baseline">
                      <Title
                        order={1}
                        style={{ fontSize: 'clamp(1.75rem, 9vw, 2.5rem)', lineHeight: 1.1 }}
                      >
                        {current.quantityOutstanding}
                      </Title>
                      <Text size="xl">{current.uomCode}</Text>
                    </Group>
                    {current.quantityPicked > 0 && (
                      <Text size="sm" c="dimmed">
                        {t('picks.alreadyPicked', {
                          picked: current.quantityPicked,
                          requested: current.quantityRequested,
                        })}
                      </Text>
                    )}
                  </Stack>
                </SimpleGrid>

                {/* Confirming the stop, which is the whole job. */}
                {canPost && !finished && (
                  <>
                    <Divider />
                    <Group align="flex-end" gap="sm" wrap="wrap">
                      {/* One serial is one unit, so the box reads 1 and will not be
                          changed -- which is exactly what legacy's handheld does
                          (AdjustInventoryFragment.setData sets it to 1 and disables it).
                          The rule is enforced on the server too (086, error 54273): a
                          disabled input is a courtesy, not a control. */}
                      <NumberInput
                        label={t('picks.quantity')}
                        value={
                          current.isSerialized
                            ? 1
                            : handheldQty === '' ? current.quantityOutstanding : handheldQty
                        }
                        onChange={setHandheldQty}
                        disabled={current.isSerialized}
                        description={current.isSerialized ? t('picks.oneAtATime') : undefined}
                        min={0}
                        max={current.quantityOutstanding}
                        size="lg"
                        w={130}
                      />
                      {/* Only for an item that tracks lots: a box on every stop would be one
                          more thing to tab past on a scanner, several hundred times a shift. */}
                      {current.isLotControlled && (
                        <TextInput
                          label={t('picks.lot')}
                          value={handheldLot}
                          onChange={(e) => setHandheldLot(e.currentTarget.value.toUpperCase())}
                          size="lg"
                          w={180}
                          maxLength={50}
                        />
                      )}
                      {/* Uppercased on the way in, because that is what the server stores:
                          usp_Serial_ResolveOrRegister uppercases, so a lower-case scan would
                          otherwise look like a serial nobody has received. */}
                      {current.isSerialized && (
                        <TextInput
                          label={t('picks.serial')}
                          value={handheldSerial}
                          onChange={(e) => setHandheldSerial(e.currentTarget.value.toUpperCase())}
                          size="lg"
                          w={180}
                          maxLength={50}
                        />
                      )}
                      <Button
                        size="lg"
                        color="invetrak"
                        leftSection={<IconHandGrab size={18} />}
                        loading={doPick.isPending}
                        disabled={
                          busy || dock === null || current.suggestedLocationId === null
                          // Held shut rather than letting the server refuse it: the picker is
                          // standing at the face and the answer is in their hand.
                          || (current.isLotControlled && handheldLot.trim() === '')
                          || (current.isSerialized && handheldSerial.trim() === '')
                        }
                        onClick={() =>
                          doPick.mutate({
                            lineId: current.pickLineId,
                            // One unit per scan for a serialized item, whatever the box says.
                            quantity: current.isSerialized
                              ? 1
                              : handheldQty === ''
                                ? current.quantityOutstanding
                                : Number(handheldQty),
                            fromLocationId: current.suggestedLocationId!,
                            lotNumber: handheldLot.trim() || undefined,
                            serialNumber: handheldSerial.trim() || undefined,
                          })
                        }
                      >
                        {t('picks.pick')}
                      </Button>
                      {currentLine && currentLine.quantityPicked > 0 && (
                        <Button
                          size="lg"
                          variant="default"
                          leftSection={<IconArrowBackUp size={18} />}
                          loading={doReverse.isPending}
                          disabled={busy}
                          onClick={() =>
                            doReverse.mutate({
                              lineId: current.pickLineId,
                              // A serialized line goes back one unit at a time, the same way
                              // it was picked -- the server refuses anything else (54273).
                              quantity: current.isSerialized ? 1 : currentLine.quantityPicked,
                              // Empty is fine when the line came from one lot: the server
                              // falls back to it. It only has to be typed for a mixed line.
                              lotNumber: handheldLot.trim() || undefined,
                              // For a serial, "mixed" means any line of more than one unit,
                              // so this is typed far more often than the lot is.
                              serialNumber: handheldSerial.trim() || undefined,
                            })
                          }
                        >
                          {t('picks.reverse')}
                        </Button>
                      )}
                    </Group>
                    {dock === null && (
                      <Text size="sm" c="orange">
                        {t('picks.stagingRequired')}
                      </Text>
                    )}
                    {current.suggestedLocationId === null && (
                      <Text size="sm" c="orange">
                        {t('picks.noFaceCannotPick')}
                      </Text>
                    )}
                  </>
                )}
              </Stack>
            </Card>

            {/*
              The one-scan path. A pallet built for this order is picked whole rather than line by
              line — legacy's LicensePlatePickFragment, which is a separate screen there and a
              second option on this one, because it is the same job.
            */}
            {canPost && !finished && (
              <Card withBorder padding="md">
                <Stack gap="sm">
                  <Group gap="xs">
                    <IconPackages size={18} />
                    <Text fw={500}>{t('picks.wholePlate')}</Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    {t('picks.wholePlateHint')}
                  </Text>
                  <Select
                    label={t('picks.plate')}
                    placeholder={t('picks.choosePlate')}
                    data={(plates.data?.items ?? []).map((lp) => ({
                      value: lp.licensePlateId,
                      label: lp.locationCode
                        ? `${lp.licensePlateNumber} — ${lp.locationCode}`
                        : lp.licensePlateNumber,
                    }))}
                    value={plateId}
                    onChange={setPlateId}
                    searchable
                    clearable
                  />
                  <Button
                    size="lg"
                    variant="default"
                    leftSection={<IconPackages size={18} />}
                    loading={doPickPlate.isPending}
                    disabled={busy || plateId === null || dock === null}
                    onClick={() => doPickPlate.mutate()}
                  >
                    {t('picks.pickWholePlate')}
                  </Button>
                </Stack>
              </Card>
            )}

            <Group grow>
              <Button
                size="lg"
                variant="default"
                leftSection={<IconArrowLeft size={18} />}
                disabled={stop === 0}
                onClick={() => setStop((s) => Math.max(0, s - 1))}
              >
                {t('picks.previous')}
              </Button>
              <Button
                size="lg"
                color="invetrak"
                rightSection={<IconArrowRight size={18} />}
                disabled={stop >= stops.length - 1}
                onClick={() => setStop((s) => Math.min(stops.length - 1, s + 1))}
              >
                {t('picks.next')}
              </Button>
            </Group>
          </>
        )}
      </Stack>

      {pick.isSuccess && (
        <ShipModal
          opened={shipOpen}
          onClose={closeShip}
          pick={pick.data}
          outstandingLines={pick.data.linesOutstanding}
          onShipped={refresh}
        />
      )}
    </>
  );
}
